// CMO IA - Centre de Decision v1
import { useState, useEffect } from "react";
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Lightbulb,
  Target,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  RefreshCw,
  Eye,
  Play,
  X,
  History,
  Zap,
  BarChart3,
  FileText,
  Calendar,
  MessageSquare,
  Loader2,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hub/cmo`;

// Helper pour les headers auth
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Composant pour afficher le niveau de confiance
function ConfidenceIndicator({ confidence, completeness }) {
  const getColor = () => {
    if (confidence >= 80) return "text-emerald-400";
    if (confidence >= 60) return "text-amber-400";
    return "text-red-400";
  };

  const getBgColor = () => {
    if (confidence >= 80) return "bg-emerald-500/20";
    if (confidence >= 60) return "bg-amber-500/20";
    return "bg-red-500/20";
  };

  return (
    <div className={`px-3 py-2 rounded-lg ${getBgColor()} flex items-center gap-3`}>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-zinc-400">Niveau de confiance</span>
          <span className={`text-sm font-bold ${getColor()}`}>{confidence.toFixed(0)}%</span>
        </div>
        <Progress value={confidence} className="h-1.5" />
      </div>
      <Badge variant="outline" className={`text-xs ${completeness === 'complete' ? 'border-emerald-500/50 text-emerald-400' : 'border-amber-500/50 text-amber-400'}`}>
        {completeness === 'complete' ? 'Données complètes' : 'Données partielles'}
      </Badge>
    </div>
  );
}

// Composant Insight Card
function InsightCard({ insight }) {
  const getIcon = () => {
    switch (insight.type) {
      case 'trend': return <TrendingUp className="w-4 h-4" />;
      case 'gap': return <AlertTriangle className="w-4 h-4" />;
      case 'opportunity': return <Lightbulb className="w-4 h-4" />;
      case 'risk': return <AlertCircle className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getTypeColor = () => {
    switch (insight.type) {
      case 'trend': return 'text-blue-400 bg-blue-500/20';
      case 'gap': return 'text-orange-400 bg-orange-500/20';
      case 'opportunity': return 'text-emerald-400 bg-emerald-500/20';
      case 'risk': return 'text-red-400 bg-red-500/20';
      default: return 'text-zinc-400 bg-zinc-500/20';
    }
  };

  const getTypeLabel = () => {
    switch (insight.type) {
      case 'trend': return 'Tendance';
      case 'gap': return 'Manque';
      case 'opportunity': return 'Opportunité';
      case 'risk': return 'Risque';
      default: return 'Donnée';
    }
  };

  return (
    <div className="p-4 bg-zinc-800/50 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getTypeColor()}`}>
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getTypeColor()}`}>
              {getTypeLabel()}
            </Badge>
            <span className="text-[10px] text-zinc-500">{insight.category}</span>
          </div>
          <h4 className="text-sm font-medium text-white mb-1">{insight.title}</h4>
          <p className="text-xs text-zinc-400 line-clamp-2">{insight.observation}</p>
          <p className="text-[10px] text-zinc-600 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {insight.data_source}
          </p>
        </div>
      </div>
    </div>
  );
}

// Composant Recommendation Card
function RecommendationCard({ recommendation, onAction }) {
  const getPriorityColor = () => {
    switch (recommendation.priority) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-amber-500 text-black';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const getPriorityLabel = () => {
    switch (recommendation.priority) {
      case 'critical': return 'Critique';
      case 'high': return 'Haute';
      case 'medium': return 'Moyenne';
      default: return 'Basse';
    }
  };

  return (
    <div className="p-4 bg-zinc-800/50 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Badge className={getPriorityColor()}>
            {getPriorityLabel()}
          </Badge>
          <span className="text-xs text-zinc-500">{recommendation.category}</span>
        </div>
        <span className="text-[10px] text-zinc-500">Effort: {recommendation.effort_level}</span>
      </div>
      
      <h4 className="text-sm font-medium text-white mb-2">{recommendation.title}</h4>
      <p className="text-xs text-zinc-400 mb-3">{recommendation.description}</p>
      
      <div className="p-2 bg-zinc-900/50 rounded-lg mb-3">
        <p className="text-[10px] text-zinc-500 mb-1">Justification CMO IA</p>
        <p className="text-xs text-zinc-300 italic">"{recommendation.justification}"</p>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-emerald-400 flex items-center gap-1">
          <Target className="w-3 h-3" />
          {recommendation.expected_impact}
        </span>
        {recommendation.actions?.length > 0 && (
          <Button 
            size="sm" 
            className="h-7 text-xs bg-blue-500 hover:bg-blue-600"
            onClick={() => onAction(recommendation)}
          >
            <Play className="w-3 h-3 mr-1" />
            Exécuter
          </Button>
        )}
      </div>
    </div>
  );
}

// Composant Decision Card
function DecisionCard({ decision, onValidate, onCancel, onView, onDelete }) {
  const getStatusBadge = () => {
    switch (decision.status) {
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">En attente</Badge>;
      case 'executed':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Exécutée</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Annulée</Badge>;
      default:
        return <Badge className="bg-zinc-500/20 text-zinc-400">{decision.status}</Badge>;
    }
  };

  const getTypeBadge = () => {
    if (decision.type === 'auto') {
      return <Badge variant="outline" className="text-[10px] border-purple-500/50 text-purple-400">Auto</Badge>;
    }
    return <Badge variant="outline" className="text-[10px] border-blue-500/50 text-blue-400">Manuel</Badge>;
  };

  return (
    <div className={`p-4 rounded-xl border transition-colors ${
      decision.status === 'pending' 
        ? 'bg-amber-500/5 border-amber-500/20' 
        : 'bg-zinc-800/50 border-white/5'
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {getTypeBadge()}
          {getStatusBadge()}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500">
            {new Date(decision.created_at).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
          {/* Bouton Supprimer */}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={() => onDelete(decision)}
            data-testid={`delete-decision-${decision.id}`}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <h4 className="text-sm font-medium text-white mb-2">{decision.title}</h4>
      <p className="text-xs text-zinc-400 mb-3">{decision.description}</p>

      {/* Indicateur de confiance */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 bg-zinc-900/50 rounded-full h-2">
          <div 
            className={`h-full rounded-full ${
              decision.confidence >= 80 ? 'bg-emerald-500' : 
              decision.confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${decision.confidence}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${
          decision.confidence >= 80 ? 'text-emerald-400' : 
          decision.confidence >= 60 ? 'text-amber-400' : 'text-red-400'
        }`}>
          {decision.confidence.toFixed(0)}%
        </span>
      </div>

      {/* Justification */}
      <div className="p-2 bg-zinc-900/50 rounded-lg mb-3">
        <p className="text-[10px] text-zinc-500 mb-1">Justification</p>
        <p className="text-xs text-zinc-300 italic line-clamp-2">"{decision.justification}"</p>
      </div>

      {/* Actions */}
      {decision.status === 'pending' && (
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            className="flex-1 h-8 text-xs bg-emerald-500 hover:bg-emerald-600"
            onClick={() => onValidate(decision)}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Valider
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            className="flex-1 h-8 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
            onClick={() => onCancel(decision)}
          >
            <XCircle className="w-3 h-3 mr-1" />
            Annuler
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            className="h-8 text-xs text-zinc-400 hover:text-white"
            onClick={() => onView(decision)}
          >
            <Eye className="w-3 h-3" />
          </Button>
        </div>
      )}

      {decision.status === 'cancelled' && decision.cancel_reason && (
        <div className="p-2 bg-red-500/10 rounded-lg">
          <p className="text-[10px] text-red-400">Raison: {decision.cancel_reason}</p>
        </div>
      )}
    </div>
  );
}

// Composant principal
export default function CMODecisionCenter() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState("decisions");
  
  // Modals
  const [showDecisionDetail, setShowDecisionDetail] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching CMO dashboard:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboard();
    toast.success("Données actualisées");
  };

  const handleValidateDecision = async (decision) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/decisions/${decision.id}/validate`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Décision validée et exécutée");
      fetchDashboard();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDecision = async () => {
    if (!showCancelDialog) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/decisions/${showCancelDialog.id}/cancel`, {
        reason: cancelReason
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Décision annulée");
      setShowCancelDialog(null);
      setCancelReason("");
      fetchDashboard();
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    } finally {
      setActionLoading(false);
    }
  };

  // Supprimer une décision
  const handleDeleteDecision = async (decision) => {
    if (!confirm(`Supprimer définitivement la décision "${decision.title}" ?`)) return;
    
    try {
      await axios.delete(`${API}/decisions/${decision.id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Décision supprimée");
      fetchDashboard();
    } catch (error) {
      console.error("Erreur suppression décision:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExecuteRecommendation = (recommendation) => {
    toast.info(`Exécution: ${recommendation.title}`);
    // En v2, créer une décision à partir de la recommandation
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-3" />
          <p className="text-zinc-400">Chargement du Centre de Décision...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-zinc-400">Impossible de charger les données</p>
          <Button onClick={handleRefresh} className="mt-4">Réessayer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Centre de Décision
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">v1</Badge>
            </h1>
            <p className="text-zinc-400 text-sm">CMO IA agit, vous arbitrez</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-white/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Indicateur de confiance global */}
      <ConfidenceIndicator 
        confidence={dashboardData.overall_confidence} 
        completeness={dashboardData.data_sources_status?.content_factory === 'connected' ? 'complete' : 'partial'}
      />

      {/* Stats rapides */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{dashboardData.insights?.length || 0}</p>
                <p className="text-xs text-zinc-500">Constats</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{dashboardData.recommendations?.length || 0}</p>
                <p className="text-xs text-zinc-500">Recommandations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{dashboardData.pending_decisions?.length || 0}</p>
                <p className="text-xs text-zinc-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-white/10 ${dashboardData.blocked_missions > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-zinc-900/50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dashboardData.blocked_missions > 0 ? 'bg-red-500/20' : 'bg-zinc-500/20'}`}>
                <AlertCircle className={`w-5 h-5 ${dashboardData.blocked_missions > 0 ? 'text-red-400' : 'text-zinc-400'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{dashboardData.blocked_missions || 0}</p>
                <p className="text-xs text-zinc-500">Bloquées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{dashboardData.decisions_executed || 0}</p>
                <p className="text-xs text-zinc-500">Exécutées</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ÉTAT STRATÉGIQUE - Vue d'ensemble des objectifs */}
      {(dashboardData.active_missions?.length > 0 || dashboardData.blocked_missions > 0) && (
        <Card className="bg-gradient-to-br from-zinc-900/80 to-zinc-800/50 border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              État Stratégique
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboardData.active_missions?.map(mission => (
              <div 
                key={mission.id} 
                className={`p-4 rounded-lg border ${
                  mission.statut === 'bloquee' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-zinc-800/50 border-white/5'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        mission.statut === 'bloquee' ? 'bg-red-500/20 text-red-400' :
                        mission.statut === 'en_cours' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {mission.statut === 'bloquee' ? '🚧 Bloquée' : 
                         mission.statut === 'en_cours' ? '▶️ En cours' : mission.statut}
                      </span>
                      {mission.assets_concernes?.[0] && (
                        <span className="text-xs text-zinc-500">{mission.assets_concernes[0]}</span>
                      )}
                    </div>
                    <h4 className="text-white font-medium">{mission.titre}</h4>
                    <p className="text-sm text-zinc-400 mt-1">{mission.objectif}</p>
                    
                    {/* Signaux business */}
                    <div className="flex gap-4 mt-3">
                      <div className="text-center">
                        <p className="text-lg font-bold text-white">{mission.leads_actuels || 0}</p>
                        <p className="text-xs text-zinc-500">Leads actuels</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-amber-400">{mission.objectif_leads || '?'}</p>
                        <p className="text-xs text-zinc-500">Objectif</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-lg font-bold ${(mission.leads_actuels || 0) >= (mission.objectif_leads || 0) ? 'text-emerald-400' : 'text-red-400'}`}>
                          {mission.objectif_leads ? `${Math.round((mission.leads_actuels || 0) / mission.objectif_leads * 100)}%` : '-'}
                        </p>
                        <p className="text-xs text-zinc-500">Progression</p>
                      </div>
                    </div>
                    
                    {/* Actions de la mission */}
                    {mission.actions_summary && (
                      <div className="flex gap-2 mt-3 text-xs">
                        <span className="text-emerald-400">✓ {mission.actions_summary.executees || 0} exécutées</span>
                        <span className="text-amber-400">⏳ {mission.actions_summary.en_cours || 0} en cours</span>
                        {mission.actions_summary.bloquees > 0 && (
                          <span className="text-red-400">🚧 {mission.actions_summary.bloquees} bloquées</span>
                        )}
                      </div>
                    )}
                    
                    {/* Raison de blocage */}
                    {mission.statut === 'bloquee' && mission.blocage_raison && (
                      <div className="mt-3 p-2 bg-red-500/10 rounded text-sm text-red-300">
                        <strong>Blocage:</strong> {mission.blocage_raison}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {(!dashboardData.active_missions || dashboardData.active_missions.length === 0) && (
              <div className="text-center py-6 text-zinc-500">
                <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune mission active</p>
                <p className="text-sm mt-1">Définissez un objectif via le Chat CMO</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Contenu principal avec tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-white/10">
          <TabsTrigger value="decisions" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Zap className="w-4 h-4 mr-2" />
            Décisions ({dashboardData.pending_decisions?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <Eye className="w-4 h-4 mr-2" />
            Constats ({dashboardData.insights?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <Lightbulb className="w-4 h-4 mr-2" />
            Recommandations ({dashboardData.recommendations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-zinc-500/20 data-[state=active]:text-zinc-400">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Tab Décisions */}
        <TabsContent value="decisions" className="space-y-4">
          {dashboardData.pending_decisions?.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Aucune décision en attente</h3>
                <p className="text-zinc-400 text-sm">CMO IA analysera les données et proposera des actions.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {dashboardData.pending_decisions?.map(decision => (
                <DecisionCard
                  key={decision.id}
                  decision={decision}
                  onValidate={handleValidateDecision}
                  onCancel={(d) => setShowCancelDialog(d)}
                  onView={(d) => setShowDecisionDetail(d)}
                  onDelete={handleDeleteDecision}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab Constats */}
        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dashboardData.insights?.map(insight => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        </TabsContent>

        {/* Tab Recommandations */}
        <TabsContent value="recommendations" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dashboardData.recommendations?.map(reco => (
              <RecommendationCard 
                key={reco.id} 
                recommendation={reco}
                onAction={handleExecuteRecommendation}
              />
            ))}
          </div>
        </TabsContent>

        {/* Tab Historique */}
        <TabsContent value="history" className="space-y-4">
          {dashboardData.recent_decisions?.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Aucun historique</h3>
                <p className="text-zinc-400 text-sm">Les décisions passées apparaîtront ici.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {dashboardData.recent_decisions?.map(decision => (
                <DecisionCard
                  key={decision.id}
                  decision={decision}
                  onValidate={() => {}}
                  onCancel={() => {}}
                  onView={(d) => setShowDecisionDetail(d)}
                  onDelete={handleDeleteDecision}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Détail Décision */}
      <Dialog open={!!showDecisionDetail} onOpenChange={() => setShowDecisionDetail(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-amber-400" />
              Détail de la décision
            </DialogTitle>
          </DialogHeader>
          
          {showDecisionDetail && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">{showDecisionDetail.title}</h3>
                <p className="text-zinc-400 mt-1">{showDecisionDetail.description}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Confiance</p>
                  <p className="text-lg font-bold text-amber-400">{showDecisionDetail.confidence}%</p>
                </div>
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Complétude données</p>
                  <p className="text-lg font-bold text-white">{showDecisionDetail.data_completeness}</p>
                </div>
              </div>
              
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-2">Facteurs de confiance</p>
                <div className="flex flex-wrap gap-2">
                  {showDecisionDetail.confidence_factors?.map((factor, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {factor}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-xs text-amber-400 mb-1">Justification CMO IA</p>
                <p className="text-sm text-white italic">"{showDecisionDetail.justification}"</p>
              </div>
              
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-2">Action</p>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-500/20 text-blue-400">{showDecisionDetail.action_type}</Badge>
                  <ArrowRight className="w-4 h-4 text-zinc-500" />
                  <Badge variant="outline">{showDecisionDetail.action_target}</Badge>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDetail(null)} className="border-white/10">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Annulation */}
      <Dialog open={!!showCancelDialog} onOpenChange={() => setShowCancelDialog(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Annuler cette décision ?
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {showCancelDialog?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">
                Raison de l'annulation (optionnel)
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Expliquez pourquoi vous annulez cette décision..."
                className="bg-zinc-800 border-white/10 text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(null)} className="border-white/10">
              Retour
            </Button>
            <Button 
              onClick={handleCancelDecision}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
